(() => {
  const root = document.documentElement;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = window.matchMedia('(pointer: fine)').matches;

  const onReady = (callback) => {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback, { once: true });
    } else {
      callback();
    }
  };

  const reveal = (scope = document) => {
    const elements = [...scope.querySelectorAll('[data-eb-reveal]:not(.is-visible)')];
    if (!elements.length) return;

    if (reducedMotion || !('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -7% 0px', threshold: 0.08 }
    );

    elements.forEach((element) => observer.observe(element));
  };

  const initializeIntro = () => {
    const intro = document.querySelector('[data-eb-intro]');
    if (!intro) {
      root.classList.remove('eb-intro-pending');
      return;
    }

    const video = intro.querySelector('[data-eb-intro-video]');
    const skip = intro.querySelector('[data-eb-intro-skip]');
    let finished = false;

    let hasSeenIntro = false;
    try {
      hasSeenIntro = window.sessionStorage.getItem('ethan-bing-film-v3') === 'seen';
    } catch (error) {
      hasSeenIntro = false;
    }

    const finish = () => {
      if (finished) return;
      finished = true;
      intro.classList.add('is-finished');
      root.classList.remove('eb-intro-pending');
      try {
        window.sessionStorage.setItem('ethan-bing-film-v3', 'seen');
      } catch (error) {
        // The reveal remains functional when storage is unavailable.
      }
      window.setTimeout(() => intro.remove(), 900);
    };

    if (hasSeenIntro || reducedMotion || root.classList.contains('shopify-design-mode')) {
      intro.classList.add('is-skipped');
      root.classList.remove('eb-intro-pending');
      return;
    }

    window.requestAnimationFrame(() => intro.classList.add('is-active'));
    skip?.addEventListener('click', finish, { once: true });

    if (video) {
      video.addEventListener('ended', finish, { once: true });
      video.addEventListener('error', finish, { once: true });
      const playPromise = video.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {
          intro.classList.add('has-fallback');
          window.setTimeout(finish, 2200);
        });
      }
    } else {
      intro.classList.add('has-fallback');
      window.setTimeout(finish, 2200);
    }

    // Never leave a visitor behind the film if a browser fails to emit `ended`.
    window.setTimeout(finish, 7500);
  };

  const initializeHeroMotion = (scope = document) => {
    if (reducedMotion || !finePointer) return;

    scope.querySelectorAll('[data-eb-hero]:not([data-eb-motion-ready])').forEach((hero) => {
      hero.setAttribute('data-eb-motion-ready', 'true');

      const update = (event) => {
        const bounds = hero.getBoundingClientRect();
        const localX = (event.clientX - bounds.left) / bounds.width;
        const localY = (event.clientY - bounds.top) / bounds.height;
        const x = Math.max(0, Math.min(1, localX));
        const y = Math.max(0, Math.min(1, localY));

        hero.style.setProperty('--eb-pointer-x', `${(x * 100).toFixed(2)}%`);
        hero.style.setProperty('--eb-pointer-y', `${(y * 100).toFixed(2)}%`);
        hero.style.setProperty('--eb-tilt-x', `${((0.5 - y) * 3.2).toFixed(2)}deg`);
        hero.style.setProperty('--eb-tilt-y', `${((x - 0.5) * 4.5).toFixed(2)}deg`);
        hero.style.setProperty('--eb-shift-x', `${((x - 0.5) * 16).toFixed(2)}px`);
        hero.style.setProperty('--eb-shift-y', `${((y - 0.5) * 10).toFixed(2)}px`);
      };

      const reset = () => {
        hero.style.setProperty('--eb-pointer-x', '58%');
        hero.style.setProperty('--eb-pointer-y', '42%');
        hero.style.setProperty('--eb-tilt-x', '0deg');
        hero.style.setProperty('--eb-tilt-y', '0deg');
        hero.style.setProperty('--eb-shift-x', '0px');
        hero.style.setProperty('--eb-shift-y', '0px');
      };

      hero.addEventListener('pointermove', update, { passive: true });
      hero.addEventListener('pointerleave', reset, { passive: true });
    });
  };

  const initializeAtmosphere = (scope = document) => {
    if (reducedMotion) return;

    scope.querySelectorAll('[data-eb-atmosphere]:not([data-eb-canvas-ready])').forEach((canvas) => {
      const context = canvas.getContext('2d');
      if (!context) return;
      canvas.setAttribute('data-eb-canvas-ready', 'true');

      let width = 0;
      let height = 0;
      let ratio = 1;
      let particles = [];
      let frame = 0;

      const createParticle = (initial = false) => ({
        x: Math.random() * width,
        y: initial ? Math.random() * height : height + Math.random() * 60,
        radius: Math.random() * 1.15 + 0.2,
        speed: Math.random() * 0.16 + 0.045,
        drift: (Math.random() - 0.5) * 0.06,
        alpha: Math.random() * 0.32 + 0.06,
        amber: Math.random() > 0.72,
      });

      const resize = () => {
        const bounds = canvas.getBoundingClientRect();
        width = Math.max(1, bounds.width);
        height = Math.max(1, bounds.height);
        ratio = Math.min(window.devicePixelRatio || 1, 1.5);
        canvas.width = Math.round(width * ratio);
        canvas.height = Math.round(height * ratio);
        context.setTransform(ratio, 0, 0, ratio, 0, 0);
        const count = Math.min(56, Math.max(24, Math.round(width / 28)));
        particles = Array.from({ length: count }, () => createParticle(true));
      };

      const draw = () => {
        if (!canvas.isConnected) {
          window.cancelAnimationFrame(frame);
          return;
        }

        context.clearRect(0, 0, width, height);
        particles.forEach((particle, index) => {
          particle.y -= particle.speed;
          particle.x += particle.drift;
          if (particle.y < -12 || particle.x < -12 || particle.x > width + 12) {
            particles[index] = createParticle();
            return;
          }

          context.beginPath();
          context.arc(particle.x, particle.y, particle.radius, 0, Math.PI * 2);
          context.fillStyle = particle.amber
            ? `rgba(176, 116, 57, ${particle.alpha})`
            : `rgba(224, 218, 207, ${particle.alpha})`;
          context.fill();
        });
        frame = window.requestAnimationFrame(draw);
      };

      resize();
      if ('ResizeObserver' in window) {
        const resizeObserver = new ResizeObserver(resize);
        resizeObserver.observe(canvas);
      } else {
        window.addEventListener('resize', resize, { passive: true });
      }
      draw();
    });
  };

  const initializeMagneticLinks = (scope = document) => {
    if (reducedMotion || !finePointer) return;

    scope.querySelectorAll('[data-eb-magnetic]:not([data-eb-magnetic-ready])').forEach((link) => {
      link.setAttribute('data-eb-magnetic-ready', 'true');
      const label = link.querySelector('span') || link;

      link.addEventListener('pointermove', (event) => {
        const bounds = link.getBoundingClientRect();
        const x = (event.clientX - bounds.left - bounds.width / 2) * 0.12;
        const y = (event.clientY - bounds.top - bounds.height / 2) * 0.16;
        label.style.transform = `translate3d(${x}px, ${y}px, 0)`;
      });
      link.addEventListener('pointerleave', () => {
        label.style.transform = 'translate3d(0, 0, 0)';
      });
    });
  };

  const initializeCursor = () => {
    const cursor = document.querySelector('[data-eb-cursor]');
    if (!cursor || reducedMotion || !finePointer) return;

    root.classList.add('eb-has-cursor');
    window.addEventListener(
      'pointermove',
      (event) => {
        cursor.style.setProperty('--eb-cursor-x', `${event.clientX}px`);
        cursor.style.setProperty('--eb-cursor-y', `${event.clientY}px`);
        cursor.classList.add('is-visible');
      },
      { passive: true }
    );

    document.addEventListener('pointerover', (event) => {
      cursor.classList.toggle('is-interactive', Boolean(event.target.closest('a, button, input, select, textarea')));
    });
    document.addEventListener('pointerdown', () => cursor.classList.add('is-pressed'));
    document.addEventListener('pointerup', () => cursor.classList.remove('is-pressed'));
    document.addEventListener('mouseleave', () => cursor.classList.remove('is-visible'));
  };

  const initializeScrollProgress = () => {
    const progress = document.querySelector('[data-eb-scroll-progress]');
    if (!progress) return;
    let scheduled = false;

    const update = () => {
      const maximum = document.documentElement.scrollHeight - window.innerHeight;
      const ratio = maximum > 0 ? window.scrollY / maximum : 0;
      progress.style.transform = `scaleX(${Math.max(0, Math.min(1, ratio))})`;
      scheduled = false;
    };

    window.addEventListener(
      'scroll',
      () => {
        if (scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(update);
      },
      { passive: true }
    );
    update();
  };

  const initialize = (scope = document) => {
    reveal(scope);
    initializeHeroMotion(scope);
    initializeAtmosphere(scope);
    initializeMagneticLinks(scope);
  };

  root.classList.add('eb-motion-ready');
  onReady(() => {
    initializeIntro();
    initialize();
    initializeCursor();
    initializeScrollProgress();
  });

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
